import { styled } from "goober";
import { type ReactNode } from "react";
import { useTweenedSeries } from "../../hooks/useTweenedSeries";
import {
  ChartTooltipCount,
  ChartTooltipDot,
  ChartTooltipHeader,
  ChartTooltipRow,
} from "../chartHover";
import { GhostPill } from "../GhostPill";
import { Text } from "../Text";
import type { DailyVolume } from "./connectorTraffic";
import { TimeSeriesChart } from "./TimeSeriesChart";
import { Panel } from "./workflowsLayout";

export type RequestVolumeSeries = {
  key: string;
  label: string;
  color: string;
  // The folded "N other connectors" aggregate — always ranked last.
  isOther?: boolean;
};

export type OverviewStat = {
  label: string;
  value: number;
  format: (value: number) => string;
};

const DAY = 86_400_000;
// A container so the two groups respond to the panel's own width (it's a grid
// column, so the viewport is the wrong axis to key off): once the stats and
// filters can't sit side by side as rows, each collapses into a vertical stack
// hugging its own edge, rather than the filters overflowing.
const Header = styled("div")`
  container-type: inline-size;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px 24px;
  flex-wrap: wrap;
`;

const StatsRow = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;

  @container (max-width: 640px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const StatItem = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 64px;
  font-variant-numeric: tabular-nums;
`;

// Sits beside the stats when they fit; below the container breakpoint the
// pickers become a right-aligned vertical stack (min-width: 0 lets it shrink to
// each picker's width so nothing overflows).
const FilterRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
  justify-content: flex-end;

  @container (max-width: 640px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }
`;

const TooltipLabel = styled("span")`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function formatDayLabel(day: number): string {
  return new Date(day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function labelStep(dayCount: number): number {
  if (dayCount <= 10) return 2;
  if (dayCount <= 40) return 7;
  return 14;
}

export function ConnectorRequestVolumePanel({
  volume,
  series,
  seriesKeyOf,
  stats,
  filters,
  nowDay,
  placeholder,
  action,
}: {
  volume: readonly DailyVolume[];
  series: readonly RequestVolumeSeries[];
  seriesKeyOf: ReadonlyMap<string, string>;
  stats: readonly OverviewStat[];
  filters: ReactNode;
  nowDay: number;
  placeholder?: boolean;
  action?: ReactNode;
}) {
  const stacked = volume.map((day) => {
    const bySeries = new Map<string, number>();
    for (const [connectorId, requests] of day.byConnector) {
      const key = seriesKeyOf.get(connectorId) ?? connectorId;
      bySeries.set(key, (bySeries.get(key) ?? 0) + requests);
    }
    return { day: day.day, total: day.total, bySeries };
  });
  const displayedStats = useTweenedSeries(
    stats.map((stat) => stat.value),
    false,
    180
  );
  const now0 = Math.floor(nowDay / DAY) * DAY;
  let nowIndex = -1;
  for (let i = 0; i < stacked.length; i++) {
    if (stacked[i]!.day <= now0) nowIndex = i;
    else break;
  }
  if (nowIndex < 0) nowIndex = 0;

  return (
    <Panel>
      <Header>
        <StatsRow aria-hidden={placeholder || undefined}>
          {stats.map((stat, i) => (
            <StatItem key={stat.label}>
              <Text variant="text-small" color="var(--text-secondary)">
                {stat.label}
              </Text>
              {placeholder ? (
                <GhostPill style={{ width: 44, height: 12, margin: "4px 0" }} />
              ) : (
                <Text variant="text-regular-md" color="var(--text-primary)">
                  {stat.format(displayedStats[i] ?? stat.value)}
                </Text>
              )}
            </StatItem>
          ))}
        </StatsRow>
        <FilterRow>{filters}</FilterRow>
      </Header>
      <TimeSeriesChart
        points={stacked.map((day) => ({
          key: day.day,
          value: day.total,
          label: formatDayLabel(day.day),
        }))}
        labelStep={labelStep(stacked.length)}
        projection={{ fromIndex: nowIndex, mode: "baseline", label: "Today" }}
        placeholder={placeholder}
        tooltip={(index) => {
          const day = stacked[index]!;
          const rows = series
            .map((s) => ({ s, value: day.bySeries.get(s.key) ?? 0 }))
            .filter((row) => row.value > 0)
            .sort((a, b) =>
              a.s.isOther === b.s.isOther
                ? b.value - a.value
                : a.s.isOther
                  ? 1
                  : -1
            );
          return (
            <>
              <ChartTooltipHeader>Connectors</ChartTooltipHeader>
              {rows.map((row) => (
                <ChartTooltipRow key={row.s.key}>
                  <ChartTooltipDot style={{ background: row.s.color }} />
                  <TooltipLabel>{row.s.label}</TooltipLabel>
                  <ChartTooltipCount>
                    {row.value.toLocaleString("en-US")}
                  </ChartTooltipCount>
                </ChartTooltipRow>
              ))}
            </>
          );
        }}
      />
      {action}
    </Panel>
  );
}
